import Vue from 'vue'
import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useWorkFlowList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refBudgetExpenseTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'costCenter.name',
      label: 'cost centre',
      sortable: true,
      thStyle: {
        width: '15%',
        minWidth: '15%',
      },
      tdClass: 'percent15',
    },
    {
      key: 'code',
      label: 'budget codes',
      sortable: true,
      thStyle: {
        width: '20%',
        minWidth: '20%',
      },
      tdClass: 'percent20',
    },
    {
      key: 'expenseCategoryName',
      label: 'sub budget (expense categories)',
      sortable: true,
      thStyle: {
        width: '20%',
        minWidth: '20%',
      },
      tdClass: 'percent20',
    },
    {
      key: 'allocated',
      label: 'allocated budget',
      sortable: true,
      thStyle: {
        width: '15%',
        minWidth: '15%',
      },
      tdClass: 'percent15',
    },
    {
      key: 'spent',
      label: 'spent',
      sortable: true,
      thStyle: {
        width: '10%',
        minWidth: '10%',
      },
      tdClass: 'percent10',
    },
    {
      key: 'remaining',
      label: 'balance',
      sortable: true,
      thStyle: {
        width: '10%',
        minWidth: '10%',
      },
      tdClass: 'percent10',
    },
    {
      key: 'usagePercentage',
      label: 'budget usage',
      sortable: true,
      thStyle: {
        width: '10%',
        minWidth: '10%',
      },
      tdClass: 'percent10',
    },
  ]

  const nonBudgetTableColumns = [
    {
      key: 'costCenter',
      label: 'cost centre',
      sortable: true,
      thStyle: { width: '25%' },
      tdClass: 'percent25',
    },
    {
      key: 'code',
      label: 'budget codes',
      sortable: true,
      thStyle: { width: '25%' },
      tdClass: 'percent25',
    },
    {
      key: 'name',
      label: 'sub budget (expense categories)',
      sortable: true,
      thStyle: { width: '35%' },
      tdClass: 'percent35',
    },
    {
      key: 'spent',
      label: 'spent',
      sortable: true,
      thStyle: { width: '15%' },
      tdClass: 'percent15',
    },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalWorkFlows = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'createdAt')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const categoryFilter = ref([])
  if (Array.isArray(route.query.category)) {
    categoryFilter.value = route.query.category
  } else if (route.query.category) {
    categoryFilter.value = [route.query.category]
  }
  const toDeletedID = ref(null)
  const type = ref('all-work-flows')
  const workFlowData = ref([])
  const createdAtFilter = ref(route.query.createdAt || '')
  let initialLoad = true
  const dataDuplicated = ref(false)

  const dataMeta = computed(() => {
    const localItemsCount = refBudgetExpenseTable.value ? refBudgetExpenseTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalWorkFlows.value,
    }
  })

  const refetchData = () => {
    refBudgetExpenseTable.value.refresh()
  }

  watch([currentPage, searchQuery, statusFilter, categoryFilter, createdAtFilter], () => {
    refetchData()
  }, { deep: true })

  const fetchWorkFlows = (ctx, callback) => {
    store
      .dispatch('app-all-work-flows/fetchWorkFlows', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        category: categoryFilter.value,
        createdAt: createdAtFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { workFlows, total } = response.data
        workFlowData.value = workFlows
        callback(workFlows)
        totalWorkFlows.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              category: categoryFilter.value,
              createdAt: createdAtFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
        dataDuplicated.value = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteWorkFlow = () => {
    store
      .dispatch('app-all-work-flows/deleteWorkFlow', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const duplicateWorkFlow = () => {
    store
      .dispatch('app-all-work-flows/duplicateWorkFlow', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        // totalWorkFlows.value += 1
        // const current = Math.ceil(totalWorkFlows.value / perPage.value)
        sortBy.value = 'createdAt'
        currentPage.value = 1
        dataDuplicated.value = true
        // refetchData()
        // toast({
        //   component: ToastificationContent,
        //   props: {
        //     title: response.data.message || '',
        //     icon: 'CheckIcon',
        //     variant: 'success',
        //   },
        // })
        Vue.swal({
          title: 'Successfully Duplicated!',
          html: `${response.data.prevName} workflow has been successfully duplicated. You may find it in the workflow listings.`,
          // eslint-disable-next-line global-require
          imageUrl: require('@/assets/images/icons/save.png'),
          reverseButtons: true,
          showCancelButton: false,
          confirmButtonText: 'Okay, Got It.',
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveWorkFlowStatusVariant = status => {
    if (status === 'active') return 'success'
    if (status === 'archived') return 'secondary'
    return 'primary'
  }

  return {
    fetchWorkFlows,
    tableColumns,
    nonBudgetTableColumns,
    perPage,
    currentPage,
    totalWorkFlows,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refBudgetExpenseTable,

    resolveWorkFlowStatusVariant,
    refetchData,

    toDeletedID,
    deleteWorkFlow,
    duplicateWorkFlow,
    workFlowData,
    dataDuplicated,

    // Extra Filters
    statusFilter,
    categoryFilter,
    createdAtFilter,
  }
}
